/**
 * Service area — the comuni named on the site and in `areaServed` JSON-LD.
 *
 * Seed list (spec §10.1): Vercelli as the base, fourteen comuni of the
 * province chosen by proximity and by the towns a searcher types (plus six
 * of the western Vercellese between Santhià and Crescentino, 2026-09-25), then
 * Novara (NO) and Casale Monferrato (AL) as the neighbouring cities outside
 * the province. Coordinates are the ISTAT centroids of
 * `src/core/data/comuni.json`.
 *
 * Sources (read 2026-09-24):
 * - `altitude`: the «Altitudine» of each comune's it.wikipedia infobox
 *   (https://it.wikipedia.org/wiki/<Comune>, m s.l.m.);
 * - `distanceKm`: OSRM driving distance from Vercelli (the base's point
 *   below) to the comune's point, public demo router.project-osrm.org on
 *   OpenStreetMap data, rounded to the km; base 0. (The Zone map drew these
 *   routes until 2026-09-28; it now shows every comune of the province from
 *   ./servedComuni.ts, without routes.)
 * Every row stays `verified: false` until the owner confirms the list —
 * the build warns about unverified rows.
 */
export interface Comune {
  key: string
  name: string
  province: 'VC' | 'NO' | 'AL'
  altitude: number | null
  distanceKm: number | null
  lat: number
  lon: number
  /** `base` = the sede's town; `neighbour` = a city outside the province, named in the zone page's closing paragraph. */
  focus?: 'base' | 'neighbour'
  /** false = indicative value, still to be checked against Wikipedia/ISTAT; the check script reports them. */
  verified: boolean
}

export const COMUNI: readonly Comune[] = [
  // Vercelli's point here is the ISTAT town centroid (45.3240 / 8.4232) and
  // deliberately differs from the sede's `seo.geo` in
  // src/sedi/verde-vercelli-2.ts (45.3225 / 8.4186, the Via dei Ciudin
  // address). Keep BOTH as they are: JSON-LD `geo` is the business address,
  // the map dot and `areaServed` are the town.
  { key: 'vercelli',      name: 'Vercelli',             province: 'VC', altitude: 130, distanceKm: 0,    lat: 45.3240, lon: 8.4232, focus: 'base', verified: false },
  { key: 'borgo',         name: 'Borgo Vercelli',       province: 'VC', altitude: 126, distanceKm: 6,    lat: 45.3572, lon: 8.4664, verified: false },
  { key: 'caresanablot',  name: 'Caresanablot',         province: 'VC', altitude: 135, distanceKm: 7,    lat: 45.3574, lon: 8.3888, verified: false },
  { key: 'quinto',        name: 'Quinto Vercellese',    province: 'VC', altitude: 140, distanceKm: 10,   lat: 45.3805, lon: 8.3622, verified: false },
  { key: 'villata',       name: 'Villata',              province: 'VC', altitude: 136, distanceKm: 11,   lat: 45.3856, lon: 8.4326, verified: false },
  { key: 'prarolo',       name: 'Prarolo',              province: 'VC', altitude: 117, distanceKm: 8,    lat: 45.2822, lon: 8.4793, verified: false },
  { key: 'asigliano',     name: 'Asigliano Vercellese', province: 'VC', altitude: 127, distanceKm: 8,    lat: 45.2612, lon: 8.4062, verified: false },
  { key: 'lignana',       name: 'Lignana',              province: 'VC', altitude: 134, distanceKm: 9,    lat: 45.2853, lon: 8.3460, verified: false },
  { key: 'desana',        name: 'Desana',               province: 'VC', altitude: 131, distanceKm: 9,    lat: 45.2687, lon: 8.3575, verified: false },
  { key: 'olcenengo',     name: 'Olcenengo',            province: 'VC', altitude: 149, distanceKm: 11,   lat: 45.3636, lon: 8.3114, verified: false },
  { key: 'stroppiana',    name: 'Stroppiana',           province: 'VC', altitude: 119, distanceKm: 12,   lat: 45.2314, lon: 8.4535, verified: false },
  { key: 'trino',         name: 'Trino',                province: 'VC', altitude: 130, distanceKm: 19,   lat: 45.1942, lon: 8.2969, verified: false },
  { key: 'santhia',       name: 'Santhià',              province: 'VC', altitude: 183, distanceKm: 22,   lat: 45.3669, lon: 8.1744, verified: false },
  // The western Vercellese between Santhià and Crescentino (owner, 2026-09-25): altitude = Wikidata P2044
  // (the value it.wikipedia's infobox shows), distance = OSRM as above, point = ISTAT centroid.
  { key: 'sangermano',    name: 'San Germano Vercellese', province: 'VC', altitude: 161, distanceKm: 15, lat: 45.3502, lon: 8.2509, verified: false },
  { key: 'tronzano',      name: 'Tronzano Vercellese',  province: 'VC', altitude: 182, distanceKm: 21,   lat: 45.3428, lon: 8.1747, verified: false },
  { key: 'palazzolo',     name: 'Palazzolo Vercellese', province: 'VC', altitude: 137, distanceKm: 24,   lat: 45.1845, lon: 8.2328, verified: false },
  { key: 'bianze',        name: 'Bianzè',               province: 'VC', altitude: 182, distanceKm: 27,   lat: 45.3074, lon: 8.1201, verified: false },
  { key: 'fontanetto',    name: 'Fontanetto Po',        province: 'VC', altitude: 143, distanceKm: 28,   lat: 45.1934, lon: 8.1917, verified: false },
  { key: 'crescentino',   name: 'Crescentino',          province: 'VC', altitude: 154, distanceKm: 31,   lat: 45.1923, lon: 8.1026, verified: false },
  { key: 'livorno',       name: 'Livorno Ferraris',     province: 'VC', altitude: 188, distanceKm: 32,   lat: 45.2817, lon: 8.0820, verified: false },
  { key: 'gattinara',     name: 'Gattinara',            province: 'VC', altitude: 263, distanceKm: 46,   lat: 45.6173, lon: 8.3713, verified: false },
  { key: 'novara',        name: 'Novara',               province: 'NO', altitude: 162, distanceKm: 24,   lat: 45.4459, lon: 8.6219, focus: 'neighbour', verified: false },
  { key: 'casale',        name: 'Casale Monferrato',    province: 'AL', altitude: 116, distanceKm: 31,   lat: 45.1350, lon: 8.4526, focus: 'neighbour', verified: false },
]


/** The base of operations; the zone table marks it and the map centres on it. */
export const BASE: Comune = COMUNI[0]
